import { GripVertical } from "lucide-react"
import * as ResizablePrimitive from "react-resizable-panels"

import { cn } from "@/lib/utils"

// Groups without an autoSaveId still call useDefaultLayout (hooks run
// unconditionally), but with nothing to read or write.
const noStorage: ResizablePrimitive.LayoutStorage = {
  getItem: () => null,
  setItem: () => {},
}

// v4 reads bare numbers as pixels; every caller was written for v2, where they
// were percentages. Numbers keep meaning percent here — pass a string with a
// unit ("240px") for anything else.
const asPercent = (size: number | string | undefined) =>
  typeof size === "number" ? `${size}%` : size

/**
 * Resizable panel group. Wrap two or more `ResizablePanel`s and separate them
 * with `ResizableHandle`. Set `orientation="vertical"` for stacked panels.
 * `autoSaveId` keeps the layout in localStorage: give every panel an `id`, and
 * pass `panelIds` when some panels render conditionally.
 */
function ResizablePanelGroup({
  className,
  autoSaveId,
  panelIds,
  ...props
}: ResizablePrimitive.GroupProps & {
  autoSaveId?: string
  panelIds?: string[]
}) {
  const savedLayout = ResizablePrimitive.useDefaultLayout({
    id: autoSaveId ?? "",
    panelIds,
    storage: autoSaveId ? localStorage : noStorage,
  })
  return (
    <ResizablePrimitive.Group
      className={cn("h-full w-full", className)}
      {...(autoSaveId && {
        defaultLayout: savedLayout.defaultLayout,
        onLayoutChanged: savedLayout.onLayoutChanged,
      })}
      {...props}
    />
  )
}

/** Numeric sizes are percentages (see asPercent). */
function ResizablePanel({
  defaultSize,
  minSize,
  maxSize,
  collapsedSize,
  style,
  ...props
}: ResizablePrimitive.PanelProps) {
  return (
    <ResizablePrimitive.Panel
      defaultSize={asPercent(defaultSize)}
      minSize={asPercent(minSize)}
      maxSize={asPercent(maxSize)}
      collapsedSize={asPercent(collapsedSize)}
      // v2 clipped panel content; v4's content element scrolls by default.
      style={{ overflow: "hidden", ...style }}
      {...props}
    />
  )
}

/**
 * Draggable divider between two panels. Pass `withHandle` to show a grip.
 * Double-click is opt-in (`disableDoubleClick={false}`): v4 resets the split
 * to the panels' defaultSize on double-click by default, which v2 never did;
 * only the conformity editor had that behaviour, via its own handler.
 */
function ResizableHandle({
  withHandle,
  className,
  disableDoubleClick = true,
  ...props
}: ResizablePrimitive.SeparatorProps & {
  withHandle?: boolean
}) {
  return (
    <ResizablePrimitive.Separator
      disableDoubleClick={disableDoubleClick}
      // In a vertical group the separator itself is horizontal, which v4
      // reports as aria-orientation="horizontal".
      className={cn(
        "relative flex w-1.5 items-center justify-center border border-grey4 bg-grey2 transition-colors after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring aria-[orientation=horizontal]:h-1 aria-[orientation=horizontal]:w-full data-[separator=active]:bg-primary/50 data-[separator=hover]:bg-primary/70 aria-[orientation=horizontal]:after:left-0 aria-[orientation=horizontal]:after:h-1 aria-[orientation=horizontal]:after:w-full aria-[orientation=horizontal]:after:-translate-y-1/2 aria-[orientation=horizontal]:after:translate-x-0",
        className,
      )}
      {...props}
    >
      {withHandle && (
        <div className="z-10 flex h-5 w-3 items-center justify-center rounded-sm border bg-border">
          <GripVertical className="h-2.5 w-2.5" />
        </div>
      )}
    </ResizablePrimitive.Separator>
  )
}

export { ResizableHandle, ResizablePanel, ResizablePanelGroup }
